import React, { useEffect, useRef, useState } from "react";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import {
    advance,
    goalsOf,
    Keeper,
    keeperIsSet,
    Kick,
    KICKS_PER_MATCH,
    kick,
    Outcome,
    readiness,
    Side,
    SoccerState,
    startMatch,
} from "./soccerGame.ts";
import { cardFaces } from "../cardFaces.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

export interface SoccerResult {
    keeper: Keeper;
    kicks: Kick[];
}

interface SoccerGameRunProps {
    queue: QuizQueue;
    keeper: Keeper;
    onFinish(result: SoccerResult): void;
}

/*
 * The pitch is drawn in a 320 x 200 box: goal mouth from x 50 to 270, crossbar
 * at y 40, goal line at y 130, penalty spot at (160, 178). Every shot is the
 * ball at the spot moved by (--dx, --dy), so these are the ends of each flight.
 */
const SPOT = { x: 160, y: 178 };
const GOAL_TARGETS = [
    { x: 84, y: 62 },
    { x: 160, y: 56 },
    { x: 236, y: 62 },
];
const SAVE_TARGETS = [
    { x: 106, y: 96 },
    { x: 160, y: 72 },
    { x: 214, y: 96 },
];
const WIDE_TARGETS = [
    { x: 30, y: 22 },
    { x: 160, y: 8 },
    { x: 290, y: 22 },
];
const ROLL_TARGET = { x: 160, y: 126 };

/** Where the keeper ends up for each dive: a lean left, a jump, a lean right. */
const DIVES = [
    { x: -40, y: 6, r: -62 },
    { x: 0, y: -14, r: 0 },
    { x: 40, y: 6, r: 62 },
];

function shotTarget(outcome: Outcome, aim: Side) {
    switch (outcome) {
        case "goal":
            return GOAL_TARGETS[aim];
        case "saved":
            return SAVE_TARGETS[aim];
        case "wide":
            return WIDE_TARGETS[aim];
        default:
            return ROLL_TARGET;
    }
}

/*
 * Banner and verdict text per outcome. Written as literal `t("...")` calls
 * rather than a lookup table so StringKeys.test.js can see every key used.
 */
function bannerText(t: (key: StringKey) => string, k: Kick): string {
    if (k.hatTrick) return t("flashCards.soccerBannerHatTrick");
    switch (k.outcome) {
        case "goal":
            return t("flashCards.soccerBannerGoal");
        case "saved":
            return t("flashCards.soccerBannerSaved");
        case "wide":
            return t("flashCards.soccerBannerWide");
        default:
            return t("flashCards.soccerBannerSkipped");
    }
}

function verdictText(t: (key: StringKey) => string, k: Kick): string {
    if (k.hatTrick) return t("flashCards.soccerBannerHatTrick");
    switch (k.outcome) {
        case "goal":
            return t("flashCards.soccerVerdictGoal");
        case "saved":
            return t("flashCards.soccerVerdictSaved");
        case "wide":
            return t("flashCards.soccerVerdictWide");
        default:
            return t("flashCards.soccerVerdictSkipped");
    }
}

/** The quiz's input and verdict colours: a save was still a right answer. */
const VERDICT_CLASS: Record<Outcome, string> = {
    goal: "correct",
    saved: "correct",
    wide: "wrong",
    skipped: "skipped",
};

function Ball({ x, y }: { x: number; y: number }) {
    return (
        <g>
            <circle cx={x} cy={y} r={8} className="soccerBall" />
            <path
                d={`M${x} ${y - 3.2} L${x + 3} ${y - 1} L${x + 1.9} ${y + 2.6} L${x - 1.9} ${y + 2.6} L${x - 3} ${y - 1} Z`}
                className="soccerBallPatch"
            />
        </g>
    );
}

/** The goal, the lines and the keeper. Decoration: every outcome is also text. */
function Pitch({ lastKick, keeperSet, showSpotBall, kicksTaken }: {
    lastKick: Kick | undefined;
    keeperSet: boolean;
    showSpotBall: boolean;
    kicksTaken: number;
}) {
    const dive = lastKick && lastKick.outcome !== "skipped" ? DIVES[lastKick.dive] : null;
    const target = lastKick ? shotTarget(lastKick.outcome, lastKick.aim) : null;

    return (
        <svg className="soccerSvg" viewBox="0 0 320 200" aria-hidden="true" focusable="false">
            {/* Pitch markings: the six-yard box and the penalty spot. */}
            <path d="M0 130 H320 M100 130 V150 H220 V130" className="soccerLine" />
            <circle cx={SPOT.x} cy={SPOT.y + 10} r={2} className="soccerSpot" />

            {/* The net, then the frame over it. */}
            <g className="soccerNet">
                {Array.from({ length: 12 }, (_, i) => (
                    <line key={`v${i}`} x1={50 + i * 20} y1={40} x2={50 + i * 20} y2={130} />
                ))}
                {Array.from({ length: 5 }, (_, i) => (
                    <line key={`h${i}`} x1={50} y1={40 + i * 20} x2={270} y2={40 + i * 20} />
                ))}
            </g>
            <path d="M50 130 V40 H270 V130" className="soccerFrame" />

            {/* Keyed on the kick so each dive plays from the start. */}
            <g
                key={`keeper-${kicksTaken}`}
                className={`soccerKeeper${dive ? " soccerKeeper--dive" : ""}${keeperSet ? " soccerKeeper--set" : ""}`}
                style={
                    dive
                        ? ({ "--kx": `${dive.x}px`, "--ky": `${dive.y}px`, "--kr": `${dive.r}deg` } as React.CSSProperties)
                        : undefined
                }
            >
                <rect x={152} y={112} width={6} height={18} rx={2} className="soccerKeeperLegs" />
                <rect x={162} y={112} width={6} height={18} rx={2} className="soccerKeeperLegs" />
                <rect x={148} y={88} width={24} height={28} rx={6} className="soccerKeeperShirt" />
                <path d="M150 94 L132 82 M170 94 L188 82" className="soccerKeeperArms" />
                <circle cx={131} cy={81} r={4.5} className="soccerKeeperGlove" />
                <circle cx={189} cy={81} r={4.5} className="soccerKeeperGlove" />
                <circle cx={160} cy={78} r={9} className="soccerKeeperHead" />
            </g>

            {lastKick && target && (
                <g
                    key={`shot-${lastKick.number}`}
                    className={`soccerShot soccerShot--${lastKick.outcome}`}
                    style={{ "--dx": `${target.x - SPOT.x}px`, "--dy": `${target.y - SPOT.y}px` } as React.CSSProperties}
                >
                    <Ball x={SPOT.x} y={SPOT.y} />
                </g>
            )}

            {showSpotBall && (
                <g key={`spot-${kicksTaken}`} className={kicksTaken > 0 ? "soccerSpotBall soccerSpotBall--next" : "soccerSpotBall"}>
                    <Ball x={SPOT.x} y={SPOT.y} />
                </g>
            )}
        </svg>
    );
}

/**
 * One match: ten penalties, each a fact (ADR 0059).
 *
 * The engine is pure (`soccerGame.ts`); this feeds it animation-frame deltas
 * and Enter presses. State lives in a ref with a snapshot published per frame,
 * as in DropGameRun, because `kick` draws from the queue (0053 §8).
 *
 * Enter marks the kick and puts the next fact up in the same render. The shot
 * plays over the goal while the child is already reading the next one, so the
 * animation is never something they wait for (0003 §1). The keeper meter only
 * decides goal or save - it never moves the match on (§2).
 *
 * rAF does not run in a hidden tab, so switching away pauses the meter for
 * free (§3), and focus stays in the box throughout, as on the quiz.
 */
export function SoccerGameRun({ queue, keeper, onFinish }: SoccerGameRunProps) {
    const t = useTranslation();
    const stateRef = useRef<SoccerState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startMatch(keeper, queue);
    }
    const [view, setView] = useState<SoccerState>(stateRef.current);
    const [typed, setTyped] = useState("");
    const [announcement, setAnnouncement] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const reported = useRef(false);

    const over = view.phase === "over";
    const set = keeperIsSet(view);
    const kicksTaken = view.kicks.length;
    const lastKick = view.kicks[kicksTaken - 1];

    useEffect(() => {
        if (over) {
            return;
        }
        let frame = 0;
        let last = performance.now();
        const tick = (now: number) => {
            const next = advance(stateRef.current!, now - last);
            last = now;
            // A frame that changed nothing (the meter is full) re-renders nothing.
            if (next !== stateRef.current) {
                stateRef.current = next;
                setView(next);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [over]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            onFinish({ keeper: view.keeper, kicks: view.kicks });
        }
    }, [over, view, onFinish]);

    // The keeper getting set is announced once per kick, not per frame (ADR
    // 0059, Accessibility). The outcome itself is in the verdict line.
    useEffect(() => {
        if (set) {
            setAnnouncement(t("flashCards.soccerKeeperSet"));
        } else {
            setAnnouncement("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [set, kicksTaken]);

    function takeKick() {
        const next = kick(stateRef.current!, typed, queue);
        if (next === stateRef.current) {
            return;
        }
        stateRef.current = next;
        setView(next);
        setTyped("");
    }

    const faces = cardFaces(view.problem);
    const countdownSeconds = Math.max(1, Math.ceil(view.countdownLeftMs / 1000));
    const goals = goalsOf(view.kicks);
    const verdictClass = lastKick ? VERDICT_CLASS[lastKick.outcome] : null;

    return (
        <div className="quizRun soccerRun">
            <div className="quizStatus soccerStatus">
                <span className="quizScore">{format(t("flashCards.soccerGoalsTemplate"), { n: goals })}</span>
                <span className="soccerKickCount">
                    {format(t("flashCards.soccerKickTemplate"), {
                        n: Math.min(kicksTaken + 1, KICKS_PER_MATCH),
                        total: KICKS_PER_MATCH,
                    })}
                </span>
            </div>

            <ol
                className="soccerRecord"
                aria-label={format(t("flashCards.soccerRecordAriaTemplate"), { goals, kicks: kicksTaken })}
            >
                {Array.from({ length: KICKS_PER_MATCH }, (_, i) => {
                    const k = view.kicks[i];
                    const state = !k ? "pending" : k.outcome === "goal" ? "goal" : "miss";
                    return <li key={i} className={`soccerRecordDot soccerRecordDot--${state}`} aria-hidden="true" />;
                })}
            </ol>

            <div className="soccerPitch">
                <Pitch
                    lastKick={lastKick}
                    keeperSet={set}
                    showSpotBall={!over}
                    kicksTaken={kicksTaken}
                />
                {view.phase === "countdown" && (
                    <div className="soccerOverlay" aria-live="assertive">
                        <p className="quizCountdownNumber">{countdownSeconds}</p>
                        <p className="soccerOverlayHint">{t("flashCards.getReady")}</p>
                    </div>
                )}
                {lastKick && (
                    <p
                        key={`banner-${lastKick.number}`}
                        className={`soccerBanner soccerBanner--${lastKick.outcome}`}
                        aria-hidden="true"
                    >
                        {bannerText(t, lastKick)}
                    </p>
                )}
            </div>

            <div className="soccerMeter" aria-hidden="true">
                <span className="soccerMeterLabel">
                    {set ? t("flashCards.soccerKeeperSetShort") : t("flashCards.soccerKeeperGettingReady")}
                </span>
                <span className="soccerMeterTrack">
                    <span
                        className={`soccerMeterFill${set ? " soccerMeterFill--set" : ""}`}
                        style={{ width: `${Math.round(readiness(view) * 100)}%` }}
                    />
                </span>
            </div>

            <form
                className="quizCard"
                onSubmit={(e) => {
                    e.preventDefault();
                    takeKick();
                }}
            >
                <p className="quizProblem">{view.phase === "countdown" ? " " : `${faces.front} =`}</p>
                <input
                    ref={inputRef}
                    className={`quizInput${verdictClass ? ` quizInput--${verdictClass}` : ""}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value.replace(/[^\d]/g, ""))}
                    onKeyDown={(e) => {
                        // As on the quiz: implicit submission on Enter does not
                        // always fire, and every kick is taken on Enter.
                        if (e.key === "Enter") {
                            e.preventDefault();
                            takeKick();
                        }
                    }}
                    // Enabled through the countdown so focus - and a phone's
                    // keyboard - is already there for the first kick.
                    disabled={over}
                    aria-label={
                        view.phase === "countdown"
                            ? t("flashCards.soccerAnswerAria")
                            : format(t("flashCards.answerForAriaTemplate"), { front: faces.front })
                    }
                />
                <button type="submit" className="quizSubmit" disabled={over}>
                    {t("flashCards.soccerShoot")}
                </button>
            </form>

            {/* Always rendered, always the same height, as on the quiz. */}
            <p
                className={`quizVerdict${verdictClass ? ` quizVerdict--${verdictClass}` : ""}`}
                aria-live="polite"
            >
                {lastKick && (
                    <>
                        {verdictText(t, lastKick)}
                        {" · "}
                        {lastKick.front} = {lastKick.answer}
                    </>
                )}
            </p>

            <p className="soccerAnnouncer" aria-live="polite">
                {announcement}
            </p>

            <p className="quizHint">{t("flashCards.soccerHint")}</p>
        </div>
    );
}
